import type { BrowserPlatform } from './browser-types'
import { appDeepLink } from '@shared/config/Config'
import { randomId, secureRandom } from '@shared/utils/random'

export const parseBooleanFlag = (value?: string): boolean => {
  return value === 'true' || value === '1'
}

export const detectPlatform = (): BrowserPlatform => {
  const ua = navigator.userAgent.toLowerCase()
  if (/android/i.test(ua)) {
    return 'android'
  }
  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'ios'
  }
  if (ua.includes('windows')) {
    return 'win32'
  }
  if (ua.includes('linux')) {
    return 'linux'
  }
  return 'darwin'
}

// __RENDERER_NAME comes from the `data-client` attribute of the shared HTML entries, so browser bundles get it too.
export const getRendererName = (): 'assistant' | 'app' | 'overlay' | 'onboarding' => {
  if (
    window.__RENDERER_NAME === 'assistant' ||
    window.__RENDERER_NAME === 'app' ||
    window.__RENDERER_NAME === 'overlay' ||
    window.__RENDERER_NAME === 'onboarding'
  ) {
    return window.__RENDERER_NAME
  }
  return 'app'
}

export const getWindowType = (): 'chat' | 'settings' | 'overlay' | 'onboarding' => {
  const rendererName = getRendererName()
  if (rendererName === 'overlay' || rendererName === 'assistant') {
    return 'overlay'
  }
  if (rendererName === 'onboarding') {
    return 'onboarding'
  }
  return 'settings'
}

export type { AppWindowType } from '@shared/types/WindowTypes'

export const isBrowserRuntime = (): boolean => {
  return window.process?.contextId === 'browser'
}

/**
 * A page of the browser build. It goes through Vite's base because the playground serves the build under `/app/`,
 * where a root-absolute `/settings.html` would leave the app.
 */
export const getBrowserPageUrl = (page: 'overlay' | 'settings'): string => {
  return `${import.meta.env.BASE_URL}${page}.html`
}

/**
 * Navigate to a highlight:// deep-link route. In browser mode, navigates
 * the current tab to the appropriate HTML page with a hash route instead
 * of attempting to open the custom protocol which only works in Electron.
 *
 * Always go through here rather than composing the URL by hand: the scheme is
 * `highlight-dev` in a dev build, so a hardcoded `highlight://` link silently
 * does nothing for anyone running one.
 */
export const navigateToHighlightRoute = (route: string): void => {
  const normalizedRoute = route.startsWith('/') ? route : `/${route}`

  if (isBrowserRuntime()) {
    const page = normalizedRoute.startsWith('/settings') ? 'settings' : 'overlay'
    window.location.assign(`${getBrowserPageUrl(page)}#${normalizedRoute}`)
    return
  }

  // `appDeepLink` drops the leading slash. Keeping it yields `highlight://` plus
  // `/route`, and the main process reads everything after the first `:/` as the
  // path — so the extra slash leaves it matching no registered route at all,
  // and the deeplink opens a window on nothing in particular.
  window.open(appDeepLink(normalizedRoute), '_blank')
}

export const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return null
    }
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalizedPayload.padEnd(normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4), '=')
    const decodedPayload = atob(padded)
    return JSON.parse(decodedPayload) as Record<string, unknown>
  } catch (error) {
    console.warn('[browser-runtime] Failed to decode JWT payload', error)
    return null
  }
}

export const isTokenExpiringSoon = (token: string, thresholdSeconds = 30): boolean => {
  const payload = parseJwtPayload(token)
  const exp = typeof payload?.exp === 'number' ? payload.exp : null
  if (!exp) {
    return true
  }

  const currentTime = Math.floor(Date.now() / 1000)
  return exp - currentTime <= thresholdSeconds
}

export const getAuthApiBaseUrl = (): string => {
  const explicitUrl = import.meta.env.VITE_HIGHLIGHT_AUTH_BASE_URL
  if (typeof explicitUrl === 'string' && explicitUrl.length > 0) {
    return explicitUrl
  }

  const chatBackend = import.meta.env.VITE_HIGHLIGHT_CHAT_BACKEND_URL
  if (typeof chatBackend === 'string' && chatBackend.length > 0) {
    return chatBackend
  }

  if (import.meta.env.DEV && import.meta.env.VITE_USE_LOCAL_HL_CHAT_BACKEND === 'true') {
    return 'http://localhost:8787/'
  }

  if (import.meta.env.DEV) {
    return 'https://chat-backend-staging.highlightai.com/'
  }

  return 'https://chat-backend.highlightai.com/'
}

export const parseDateValue = (value: unknown): Date => {
  if (value instanceof Date) {
    return value
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value)
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate
    }
  }
  return new Date()
}

export const getStringField = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

export const generateSecureId = (): string => randomId()

export const generateSecureHex = (byteLength = 16): string => {
  try {
    const bytes = new Uint8Array(byteLength)
    window.crypto.getRandomValues(bytes)
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  } catch (error) {
    console.warn('[browser-runtime] Failed to generate secure hex', error)
    return `fallback-${Date.now()}-${Math.floor(secureRandom() * 1000000)}`
  }
}

export const copyText = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
    return false
  } catch (error) {
    console.warn('[browser-runtime] Failed to copy text', error)
    return false
  }
}
